import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { AccuracyComparisonHeader } from "./AccuracyComparisonHeader";
import { Cap2AuditTable } from "./Cap2AuditTable";
import {
  useLinkShadowTemplate,
  useShadowCandidates,
  useShadowTemplate,
} from "./ShadowBetaServices";

/**
 * Empty state for a template with no shadow: pick an eligible LLM screening
 * template and link it in place, enabling the beta view without leaving the
 * page.
 */
const LinkShadowPanel = ({ templateId }) => {
  const [candidateId, setCandidateId] = useState("");
  const { data: candidates, isLoading } = useShadowCandidates(templateId);
  const linkShadow = useLinkShadowTemplate();
  const noCandidates = !isLoading && (candidates || []).length === 0;

  return (
    <Box sx={{ p: 3, maxWidth: 480 }} data-testid="cap2-link-shadow-panel">
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        This template has no CAP 2.0 shadow configured. Link an LLM screening
        template as its shadow to enable the beta view.
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        label="LLM screening template"
        value={candidateId}
        onChange={(event) => setCandidateId(event.target.value)}
        disabled={isLoading || noCandidates}
        helperText={
          noCandidates
            ? "No eligible templates. A candidate needs a promoted LLM " +
              "screening config and this template's ground-truth dataset."
            : undefined
        }
        data-testid="cap2-shadow-candidate-select"
      >
        {(candidates || []).map((candidate) => (
          <MenuItem key={candidate.id} value={candidate.id}>
            {candidate.name}
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={!candidateId || linkShadow.isLoading}
        onClick={() =>
          linkShadow.mutate({ templateId, shadowTemplateId: candidateId })
        }
        data-testid="cap2-link-shadow-button"
      >
        Link as shadow
      </Button>
    </Box>
  );
};

LinkShadowPanel.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

/**
 * The CAP 2.0 (Beta) view body: preview banner, accuracy header, and the
 * shadow-comparison audit table. Shared by the standalone /beta route and
 * the in-place v2 toggle on the production Audit History page.
 */
export const Cap2BetaContent = ({ templateId }) => {
  const { data: shadow, isLoading } = useShadowTemplate(templateId);
  // Field definitions ride on the shadow response itself.
  const fields = shadow?.llm_screening_fields || [];

  return (
    <Box data-testid="cap2-beta-content">
      <Alert
        severity="info"
        icon={false}
        sx={{ mb: 2 }}
        data-testid="cap2-beta-banner"
      >
        <strong>BETA · Preview only.</strong> These are CAP 2.0 (AA-LLM)
        predictions shown alongside production screening. Nothing here is of
        record - no value is written to the CAP system of record.
      </Alert>
      {isLoading && (
        <Box sx={{ textAlign: "center", p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!isLoading && !shadow && <LinkShadowPanel templateId={templateId} />}
      {!isLoading && shadow && (
        <>
          <AccuracyComparisonHeader templateId={templateId} />
          <Cap2AuditTable
            templateId={templateId}
            fields={fields}
            groundTruthDatasetId={shadow?.ground_truth_dataset_fk}
          />
        </>
      )}
    </Box>
  );
};

Cap2BetaContent.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

export default Cap2BetaContent;
